document.addEventListener('alpine:init', () => {
  const APP_BASE = '/apps/wishlist/shopify';

  const LS_ITEMS = 'faf:wishlist';
  const LS_PENDING = 'faf:wishlist:pending';
  const LS_OWNER = 'faf:wishlist:owner';
  const LS_SYNCED = 'faf:wishlist:synced_at';
  const LS_SHARE = 'faf:wishlist:share_url';

  const LEGACY_GUEST_KEYS = ['faf-wish-list', 'faf-wish-list-guest'];

  const DEBOUNCE_MS = 800;
  const MAX_ITEMS = 50;

  const SNAPSHOT_TRUST_MS = 60000;

  const CUSTOMER_ID = window.customerId ? String(window.customerId) : '';

  let flushTimer = null;

  function now() {
    return new Date().toISOString();
  }

  function readJson(key, fallback) {
    try {
      return JSON.parse(localStorage.getItem(key)) ?? fallback;
    } catch (error) {
      return fallback;
    }
  }

  function writeJson(key, value) {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {

    }
  }

  function normaliseItems(value = {}) {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      return {};
    }

    return Object.entries(value).reduce((items, [rawHandle, rawVariantId]) => {
      const handle = String(rawHandle || '').trim();

      if (!handle) return items;

      const variantId = Number(rawVariantId || 0);

      items[handle] = Number.isFinite(variantId) ? Math.max(0, variantId) : 0;

      return items;
    }, {});
  }

  function sortedEntries(items = {}) {
    return Object.entries(items).sort(([a], [b]) => a.localeCompare(b));
  }

  async function api(method, path, body) {
    const response = await fetch(APP_BASE + path, {
      method,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: body === undefined ? undefined : JSON.stringify(body)
    });

    if (!response.ok) {
      throw new Error(`Wishlist sync returned ${response.status}`);
    }

    return response.json();
  }

  function isVariantRuleUnavailable(rule = null) {
    if (!rule) return false;

    if (typeof rule.purchasable === 'boolean') {
      return !rule.purchasable;
    }

    if (rule.available === false) return true;

    const inventoryQuantity = Number(rule.inventoryQuantity);

    return Boolean(
      rule.tracksInventory &&
      rule.inventoryPolicy !== 'continue' &&
      Number.isFinite(inventoryQuantity) &&
      inventoryQuantity <= 0
    );
  }

  Alpine.store('wishlist', {
    items: {},
    pending: [],
    isGuest: !CUSTOMER_ID,
    loading: false,
    sharing: false,
    shareUrl: null,

    get products() {
      return this.items;
    },

    get handles() {
      return Object.keys(this.items);
    },

    get count() {
      return this.handles.length;
    },


    init() {
      const storedOwner = localStorage.getItem(LS_OWNER);
      let local = normaliseItems(readJson(LS_ITEMS, {}));

      this.pending = Array.isArray(readJson(LS_PENDING, [])) ? readJson(LS_PENDING, []) : [];


      if (storedOwner === null) {
        for (const key of LEGACY_GUEST_KEYS) {
          local = { ...normaliseItems(readJson(key, {})), ...local };
        }
      }

      const owner = storedOwner || 'guest';

      if (this.isGuest) {
        this.items = local;
        localStorage.setItem(LS_OWNER, 'guest');
        this.persist();
        this.updateCountBadges();
        return;
      }


      if (owner !== 'guest' && owner !== CUSTOMER_ID) {
        local = {};
        this.pending = [];
        writeJson(LS_PENDING, []);
        localStorage.removeItem(LS_SHARE);
        localStorage.removeItem(LS_SYNCED);
      }

      this.shareUrl = localStorage.getItem(LS_SHARE) || null;

      const seed = normaliseItems(window.fafWishlistSeed || {});

      if (owner !== CUSTOMER_ID) {

        const merged = {
          ...normaliseItems(window.fafWishlistLegacySeed || {}),
          ...seed
        };

        for (const [handle, variantId] of Object.entries(local)) {
          if (!(handle in merged) || variantId > 0) {
            merged[handle] = variantId;
          }
        }

        this.items = merged;

        const changed =
          JSON.stringify(sortedEntries(merged)) !== JSON.stringify(sortedEntries(seed));

        if (changed && Object.keys(merged).length) {
          this.queue({ op: 'merge', items: { ...merged }, occurredAt: now() });
        }
      } else {
        const syncedAt = Number(localStorage.getItem(LS_SYNCED) || 0);
        const localIsFresh = Date.now() - syncedAt < SNAPSHOT_TRUST_MS;

        this.items = localIsFresh ? { ...local } : { ...seed };


        for (const op of this.pending) {
          if (op.op === 'add') this.items[op.handle] = op.variantId || 0;
          if (op.op === 'remove') delete this.items[op.handle];
        }
      }

      localStorage.setItem(LS_OWNER, CUSTOMER_ID);
      this.persist();
      this.updateCountBadges();


      if (this.pending.length) this.scheduleFlush();
    },

    has(handle) {
      return !!handle && handle in this.items;
    },

    hasProduct(handle) {
      return this.has(handle);
    },

    toggle(handle, variantId = 0) {
      return this.has(handle) ? this.remove(handle) : this.add(handle, variantId);
    },

    add(handle, variantId = 0) {
      if (!handle) return false;

      if (!this.has(handle) && this.count >= MAX_ITEMS) {
        return false;
      }

      this.items[handle] = Number(variantId) || 0;
      this.persist();
      this.updateCountBadges();
      this.queue({ op: 'add', handle, variantId: Number(variantId) || 0, occurredAt: now() });

      return true;
    },

    remove(handle) {
      if (!handle || !this.has(handle)) return false;

      delete this.items[handle];
      this.persist();
      this.updateCountBadges();
      this.queue({ op: 'remove', handle, occurredAt: now() });

      return true;
    },

    updateVariant(handle, variantId = 0) {
      if (!this.has(handle)) return false;

      return this.add(handle, variantId);
    },

    async share() {
      if (this.isGuest || this.sharing) return null;

      this.sharing = true;

      try {
        const data = await api('POST', '/share', {});

        if (data.url) {
          this.shareUrl = data.url;
          localStorage.setItem(LS_SHARE, data.url);
        }

        return data.url || null;
      } catch (error) {
        console.warn('Wishlist share failed:', error);
        return null;
      } finally {
        this.sharing = false;
      }
    },

    queue(op) {

      if (this.isGuest) return;

      this.pending.push(op);
      writeJson(LS_PENDING, this.pending);
      this.scheduleFlush();
    },

    scheduleFlush() {
      clearTimeout(flushTimer);
      flushTimer = setTimeout(() => this.flush(), DEBOUNCE_MS);
    },

    async flush() {
      clearTimeout(flushTimer);

      if (this.isGuest || !this.pending.length) return;

      const batch = this.pending.slice();

      try {
        const data = await api('POST', '', { ops: batch });

        this.pending = this.pending.slice(batch.length);
        writeJson(LS_PENDING, this.pending);
        this.adopt(data);
      } catch (error) {

        console.warn('Wishlist sync failed:', error);
      }
    },

    adopt(data = {}) {
      this.items = normaliseItems(data.items || {});
      this.persist();
      this.updateCountBadges();


      localStorage.setItem(LS_SYNCED, String(Date.now()));

      if (data.shareUrl) {
        this.shareUrl = data.shareUrl;
        localStorage.setItem(LS_SHARE, data.shareUrl);
      }
    },

    persist() {
      writeJson(LS_ITEMS, this.items);
    },

    updateCountBadges() {
      document.querySelectorAll('.js-wishlist-count').forEach((badge) => {
        badge.innerText = this.count;
        badge.classList.toggle('hidden', this.count === 0);
      });
    }
  });

  Alpine.data('wishlistHeart', (config = {}) => ({
    handle: config.handle || '',
    productId: Number(config.productId || 0),
    variantId: Number(config.variantId || 0),
    source: config.source || 'wishlist',
    analytics: config.analytics || {},
    interacted: false,
    locked: false,
    feedback: '',
    feedbackTimer: null,

    get saved() {
      return this.$store.wishlist.has(this.handle);
    },

    handleVariantChange(event) {
      const detail = event.detail || {};
      const eventProductId = Number(detail.productId || 0);

      if (eventProductId && eventProductId !== this.productId) return;

      if (detail.productHandle && detail.productHandle !== this.handle) return;

      this.variantId = Number(detail.variantId || 0);
    },

    showFeedback(message, duration) {
      this.locked = true;
      this.interacted = true;
      this.feedback = message;

      clearTimeout(this.feedbackTimer);

      this.feedbackTimer = setTimeout(() => {
        this.feedback = '';
        this.locked = false;
      }, duration);
    },

    analyticsProduct() {
      return {
        ...this.analytics,

        productId:
          this.productId ||
          this.analytics.productId,

        variantId:
          this.variantId ||
          this.analytics.variantId
      };
    },

    toggle() {
      if (
        this.locked ||
        !this.handle ||
        !this.productId
      ) {
        return;
      }

      const wasSaved =
        this.saved;

      const changed =
        this.$store.wishlist.toggle(
          this.handle,
          this.variantId
        );

      if (!changed) {

        if (!wasSaved) {
          window.FAFAnalytics?.custom(
            'faf_wishlist_limit_reached',
            {
              limit: 50,
              source:
                this.source
            }
          );

          this.showFeedback(
            'Wishlist full',
            1600
          );
        }

        return;
      }

      if (wasSaved) {
        window.FAFAnalytics?.custom(
          'faf_remove_from_wishlist',
          {
            product_id:
              String(
                this.productId || ''
              ),

            variant_id:
              String(
                this.variantId || ''
              ),

            source:
              this.source,

            reason:
              'heart_toggle'
          }
        );
      } else {
        window.FAFAnalytics
          ?.addToWishlist(
            this.analyticsProduct(),
            {
              source:
                this.source
            }
          );
      }

      this.showFeedback(
        wasSaved
          ? 'Removed'
          : 'Saved',
        700
      );
    }
  }));

  function wishlistHandleIsSafe(handle = '') {
    return /^[a-z0-9][a-z0-9-]*$/i.test(handle);
  }

  function wishlistSearchUrl(handles = []) {
    const root = window.Shopify?.routes?.root || '/';

    const parameters = new URLSearchParams({
      q: `(${handles.map((handle) => `handle:${handle}`).join(' OR ')})`,
      type: 'product',
      'options[prefix]': 'none',
      'options[unavailable_products]': 'last',
      section_id: 'wishlist-search-results'
    });

    return `${root}search?${parameters.toString()}`;
  }


  Alpine.data('wishlistRenderer', () => {

    let loadedSignature = '';
    let renderPromise = null;

    return {
      renderedCount: 0,

      validHandles() {
        return Array.from(
          new Set(
            this.$store.wishlist.handles
              .map((handle) => String(handle || '').trim())
              .filter(wishlistHandleIsSafe)
          )
        );
      },

      cards() {
        return Array.from(this.$root.querySelectorAll('[data-wishlist-search-card]'));
      },

      async fetchBatch(handles) {
        const response = await fetch(wishlistSearchUrl(handles), {
          headers: {
            Accept: 'text/html',
            'X-Requested-With': 'XMLHttpRequest'
          },
          cache: 'no-store'
        });

        if (!response.ok) {
          throw new Error(`Wishlist search returned ${response.status}`);
        }

        const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
        const requested = new Set(handles);
        const results = {};

        parsed.querySelectorAll('[data-wishlist-search-card]').forEach((card) => {
          const handle = String(card.getAttribute('data-wishlist-handle') || '').trim();

          if (requested.has(handle)) {
            results[handle] = card.outerHTML;
          }
        });

        return results;
      },

      syncCards(handles, results = {}) {
        const anchor = this.$refs.searchAnchor;

        if (!anchor) return;

        const wanted = new Set(handles);

        this.cards().forEach((card) => {
          if (!wanted.has(card.getAttribute('data-wishlist-handle'))) {
            Alpine.destroyTree?.(card);
            card.remove();
          }
        });

        const present = new Set(
          this.cards().map((card) => card.getAttribute('data-wishlist-handle'))
        );

        handles.forEach((handle) => {
          if (present.has(handle) || !results[handle]) return;

          const template = document.createElement('template');

          template.innerHTML = results[handle].trim();

          const card = template.content.firstElementChild;

          if (!card) return;

          Alpine.mutateDom(() => {
            anchor.before(card);
            Alpine.initTree(card);
            window.FAFSections?.enhance?.(
              card,
              { source: 'wishlist-card' }
            );
          });
        });

        this.renderedCount = this.cards().length;
      },

      render() {
        if (renderPromise) return renderPromise;

        const handles = this.validHandles();
        const signature = handles.join('|');

        if (signature === loadedSignature) {
          this.syncCards(handles);
          return Promise.resolve();
        }

        const present = new Set(
          this.cards().map((card) => card.getAttribute('data-wishlist-handle'))
        );

        if (handles.every((handle) => present.has(handle))) {
          this.syncCards(handles);
          loadedSignature = signature;
          return Promise.resolve();
        }

        renderPromise = (async () => {
          this.$store.wishlist.loading = true;

          try {
            this.syncCards(handles, await this.fetchBatch(handles));
            loadedSignature = signature;
          } catch (error) {

            console.warn('Wishlist search failed:', error);
          }
        })().finally(() => {
          this.$store.wishlist.loading = false;
          renderPromise = null;


          if (this.validHandles().join('|') !== signature) {
            this.$nextTick(() => this.render());
          }
        });

        return renderPromise;
      }
    };
  });

  function createSwipeState() {
    return {
      startX: 0,
      dragStartOffset: 0,
      deltaX: 0,
      maxOffset: 118,
      revealed: false,

      onStart(e) {
        const t = e.touches?.[0] || e;
        this.startX = t.clientX;
        this.dragStartOffset = this.revealed ? -this.maxOffset : 0;
      },

      onMove(e) {
        const t = e.touches?.[0] || e;
        const target = this.dragStartOffset + (t.clientX - this.startX);

        this.deltaX = Math.max(-this.maxOffset, Math.min(0, target));
      },

      onEnd() {
        if (this.deltaX < -this.maxOffset / 2) {
          this.revealed = true;
          this.deltaX = -this.maxOffset;
        } else {
          this.closeReveal();
        }
      },

      toggleReveal() {
        this.revealed = !this.revealed;
        this.deltaX = this.revealed ? -this.maxOffset : 0;
      },

      closeReveal() {
        this.revealed = false;
        this.deltaX = 0;
      }
    };
  }

  Alpine.data('wishlistLineItem', (config = {}) => ({
    ...createSwipeState(),

    productHandle: config.productHandle || null,
    productId: config.productId || 0,
    variantId: Number(config.variantId || 0),
    preLaunch: config.preLaunch === true,
    productGender: config.productGender || '',
    productCategory: config.productCategory || '',
    variantLabel: config.variantLabel || '',
    variantPrice: config.variantPrice || '',
    variantCompare: config.variantCompare || '',
    productLimit: Number(config.productLimit || 0),
    variantRules: config.variantRules || {},

    visible: true,
    ready: false,
    disabled: false,
    sizePickerOpen: false,
    actionState: 'idle',
    actionMessage: '',
    actionTimer: null,

    get selectedVariantRule() {
      const variantId = Number(this.variantId || 0);

      if (!variantId) return null;

      return this.variantRules[String(variantId)] || this.variantRules[variantId] || null;
    },

    get selectedVariantUnavailable() {
      return isVariantRuleUnavailable(this.selectedVariantRule);
    },

    get purchaseLocked() {
      return this.selectedVariantUnavailable;
    },

    isVariantUnavailable(variantId) {
      const rule =
        this.variantRules[String(variantId)] ||
        this.variantRules[variantId] ||
        null;

      return isVariantRuleUnavailable(rule);
    },

    init() {
      if (!this.productHandle) return;

      this.$store.cart?.registerProductRule(this.productId, this.productLimit, this.variantRules);

      const storedVariantId = Number(this.$store.wishlist.items[this.productHandle] || 0);

      const storedVariantRule =
        this.variantRules[String(storedVariantId)] || this.variantRules[storedVariantId] || null;

      const variantEntries = Object.entries(this.variantRules || {});
      const singleVariantId = variantEntries.length === 1 ? Number(variantEntries[0][0] || 0) : 0;
      const singleVariantRule = singleVariantId ? variantEntries[0][1] : null;

      if (storedVariantRule) {
        this.variantId = storedVariantId;
        this.variantLabel = storedVariantRule.title || '';
        this.variantPrice = storedVariantRule.price || this.variantPrice;
        this.variantCompare = storedVariantRule.compareAt || this.variantCompare;
      } else if (!storedVariantId && singleVariantRule) {
        this.variantId = singleVariantId;
        this.variantLabel = singleVariantRule.title || '';
        this.variantPrice = singleVariantRule.price || this.variantPrice;
        this.variantCompare = singleVariantRule.compareAt || this.variantCompare;
      } else {

        this.variantId = 0;
        this.variantLabel = '';
      }

      this.$nextTick(() => {
        this.ready = true;

        const image = this.$root.querySelector('.wishlist-image');

        if (!image) return;

        const revealImage = () => image.classList.add('lazyloaded');

        if (image.complete && image.naturalWidth) {
          requestAnimationFrame(revealImage);
          return;
        }

        image.addEventListener('load', revealImage, { once: true });
      });
    },

    formatSizeLabel(size) {
      if (!size) return 'Select Size';

      return window.FAFSizes?.format?.(size, {
        gender: this.productGender,
        category: this.productCategory,
        preference:
          this.$store.sizeSystem?.preference ||
          window.FAFSizes?.preference
      }) || size;
    },

    setActionState(state = 'idle', message = '', duration = 0) {
      clearTimeout(this.actionTimer);

      this.actionState = state;
      this.actionMessage = message;

      if (duration > 0) {
        this.actionTimer = setTimeout(() => {
          this.actionState = 'idle';
          this.actionMessage = '';
        }, duration);
      }
    },

    openSizePicker() {
      if (!this.ready || this.disabled) return;

      this.sizePickerOpen = !this.sizePickerOpen;
    },

    closeSizePicker() {
      this.sizePickerOpen = false;
    },

    handleMobileBagClick() {
      if (
        !this.ready ||
        this.disabled ||
        this.purchaseLocked
      ) {
        return;
      }

      if (this.variantId) {
        this.moveToCart();
        return;
      }

      this.sizePickerOpen = true;
    },

    handleBagClick() {
      if (
        !this.ready ||
        this.disabled ||
        this.purchaseLocked
      ) {
        return;
      }

      if (this.variantId) {
        this.moveToCart();
        return;
      }

      this.sizePickerOpen = true;
    },

    selectWishlistVariant(variantId, variantLabel = '') {
      const variantRule =
        this.variantRules[String(variantId)] || this.variantRules[variantId] || null;

      if (!variantId || this.disabled || isVariantRuleUnavailable(variantRule)) {
        return;
      }

      this.variantId = Number(variantId);
      this.variantLabel = variantLabel || variantRule?.title || this.variantLabel;
      this.variantPrice = variantRule?.price || this.variantPrice;
      this.variantCompare = variantRule?.compareAt || '';

      this.$store.wishlist.updateVariant(this.productHandle, this.variantId);
      window.FAFAnalytics?.custom(
        'faf_wishlist_variant_selected',
        {
          product_id:
            String(this.productId),
          variant_id:
            String(this.variantId),
          source:
            'wishlist_drawer'
        }
      );
      this.closeSizePicker();

      this.setActionState('updated', `${this.formatSizeLabel(this.variantLabel)} selected`, 1800);
    },

    removeFromWishlist() {
      if (!this.productHandle || this.disabled) return;

      this.disabled = true;
      this.setActionState('removing', 'Removing…');

      setTimeout(() => {
        const removed =
          this.$store.wishlist.remove(
            this.productHandle
          );

        if (removed) {
          window.FAFAnalytics?.custom(
            'faf_remove_from_wishlist',
            {
              product_id:
                String(
                  this.productId
                ),
              variant_id:
                String(
                  this.variantId || ''
                ),
              source:
                'wishlist_drawer',
              reason:
                'remove_button'
            }
          );
        }

        this.visible = false;
      }, 100);
    },

    moveToCart() {
      if (
        !this.variantId ||
        !this.$store.cart ||
        this.disabled ||
        this.purchaseLocked
      ) {
        return;
      }

      this.disabled = true;
      this.setActionState('adding', 'Adding to Bag…');

      const variantToAdd = Number(this.variantId);

      this.$store.cart.addVariant(
        variantToAdd,
        (error) => {
          if (error) {
            this.disabled = false;
            this.setActionState('error', 'Could not add to Bag', 3000);
            return;
          }

          this.setActionState('added', 'Added to Bag');
          window.FAFAnalytics?.custom(
            'faf_remove_from_wishlist',
            {
              product_id:
                String(this.productId),
              variant_id:
                String(variantToAdd),
              source:
                'wishlist_drawer',
              reason:
                'move_to_cart'
            }
          );
          setTimeout(() => {
            this.$store.wishlist.remove(this.productHandle);
            this.visible = false;
          }, 450);
        },
        null,
        null,
        'wishlist',
        {
          productId: this.productId,
          productLimit: this.productLimit,
          inventoryLimit: this.$store.cart.inventoryLimitForVariant(variantToAdd),
          source: 'wishlist_move_to_cart'
        }
      );
    }
  }));
});
